import { useCallback, useEffect, useState } from 'react';

import { ArrowLeftIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { FilterEmptyState } from '@/components/ui/custom/FilterEmptyState';
import { cn } from '@/lib/utils';

import { CheckboxFilter } from './filterTypes/CheckboxFilter';
import { RadioFilter } from './filterTypes/RadioFilter';
import { TextFilter } from './filterTypes/TextFilter';
import { ConditionMode, FilterInputType, FilterOption } from './types';

interface FilterOptionsPanelProps {
  title: string;
  type: FilterInputType;
  options: FilterOption[];
  placeholder?: string;
  condition?: boolean;
  value?: string | string[];
  onValueChange: (value: string | string[] | undefined, exclude?: boolean) => void;
  exclude?: boolean;
  onBack: () => void;
  onSelectOpenChange?: (open: boolean) => void;
  /** Show the "Clear · All" bulk actions for checkbox filters. Defaults to true. */
  showBulkActions?: boolean;
}

export const FilterOptionsPanel = ({
  title,
  type,
  options,
  placeholder,
  condition,
  value,
  onValueChange,
  exclude,
  onBack,
  onSelectOpenChange,
  showBulkActions = true,
}: FilterOptionsPanelProps) => {
  const { t } = useTranslation();
  const [conditionMode, setConditionMode] = useState<ConditionMode>(exclude ? 'is not' : 'is');

  useEffect(() => {
    setConditionMode(exclude ? 'is not' : 'is');
  }, [exclude]);

  const handleCheckboxChange = useCallback(
    (values: string | string[]) => {
      onValueChange(values, conditionMode === 'is not');
    },
    [onValueChange, conditionMode]
  );

  const handleSelectAll = () => {
    const allValues = options.map((opt) => opt.value);
    onValueChange(allValues, conditionMode === 'is not');
  };

  const handleClear = () => {
    onValueChange(undefined);
    setConditionMode('is');
  };

  const handleConditionModeChange = (mode: ConditionMode) => {
    setConditionMode(mode);
    // Update exclude flag on stored filter if there are values
    if (value && (value as string[]).length > 0) {
      onValueChange(value, mode === 'is not');
    }
  };

  const hasValue = Array.isArray(value) ? value.length > 0 : Boolean(value);
  const isEmpty = type !== 'text' && options.length === 0 && !hasValue;

  return (
    <div className={cn('flex flex-col', isEmpty && 'h-40')}>
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2">
          <Button
            onClick={onBack}
            variant="ghost-neutral"
            size="sm"
            className="size-4 min-w-4 p-0"
            aria-label={t('common.back')}
          >
            <ArrowLeftIcon size={16} />
          </Button>
          <h3 className="text-sm leading-6 font-semibold">{title}</h3>
        </div>

        {type === 'checkbox' && showBulkActions && !isEmpty && (
          <div className="flex items-center gap-2 pr-1">
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-sm font-medium text-primary hover:no-underline"
              onClick={handleClear}
            >
              {t('common.clear')}
            </Button>
            <span className="text-sm font-medium text-primary">·</span>
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-sm font-medium text-primary hover:no-underline"
              onClick={handleSelectAll}
            >
              {t('common.all')}
            </Button>
          </div>
        )}
      </div>

      {isEmpty ? (
        <FilterEmptyState text={t('common.no_options_available')} />
      ) : (
        <div className="max-h-80 overflow-y-auto">
          {type === 'checkbox' && (
            <CheckboxFilter
              options={options}
              selectedValues={(value as string[]) || []}
              onSelectionChange={handleCheckboxChange}
              condition={condition}
              conditionMode={conditionMode}
              onConditionModeChange={handleConditionModeChange}
              onSelectOpenChange={onSelectOpenChange}
            />
          )}

          {type === 'radio' && (
            <RadioFilter
              options={options}
              selectedValue={value as string | undefined}
              onSelectionChange={onValueChange}
            />
          )}

          {type === 'text' && (
            <TextFilter
              value={value as string | undefined}
              placeholder={placeholder}
              onApply={onValueChange}
            />
          )}
        </div>
      )}
    </div>
  );
};
